import React, { useState, useEffect, useRef, useMemo } from "react";
import Heading from "../../elements/heading";
import Checkbox from "../../elements/checkbox";
import PrimaryButton from "../../elements/primary-button";
import BodyText from "../../elements/body-text";
import api from "../../utils/api";
import { useSelector } from "react-redux";
import RadioButtonGroup from "../../elements/radio-button-group";
import ArvatoWidget from "../arvato-widget";
import { Icon } from "../../elements";
import BankCardInformation from "../bank-card-information";
import ConfirmationModal from "../../react-components/confirmation-modal";
import PrimaryLink from "../../elements/primary-link";
import { selectUserState } from "../../selectors/application";
import { S_GEAR_SAVE_PAYMENT, webStorage } from "../../utils/webStorage";
import useFetchBankCards from "../../hooks/useFetchBankCards";
import { pdpOrderPreviewSelector, pdpOutOfStockSelector, pdpPlanTypeSelector, pdpSavePaymentSelector, pdpSummarySelector } from "../../selectors/pdp";
import { html2Text, isEmptyObject } from "../../utils/siteUtils";
import { bankCardSubTypes, planType } from "../../utils/lynkco-constants";
import useQuery from "../../utils/useQuery";
import { multiBindConsents } from "../../services/consentService";
import "./style.scss";
const PdpCardInfo = ({ onSubmit, fields }) => {
  /*Redux Start*/
  const user = useSelector(selectUserState);
  const pdpSummary = useSelector(pdpSummarySelector);
  const pdpPlanType = useSelector(pdpPlanTypeSelector);
  const pdpOutOfStock = useSelector(pdpOutOfStockSelector);
  const pdpOrderPreview = useSelector(pdpOrderPreviewSelector);
  const pdpSavePayment = useSelector(pdpSavePaymentSelector);
  /*Redux End*/
  const query = useQuery();
  const { lid, lno } = query;
  const { bacArvatoOptions, mtmArvatoOptions, heading, headingType, submitCTA, removeCardModal, notice, disclaimer, paymethodMissingMessage, ctaNameAddCard, colorSchemeAddCard, iconNameAddCard, cardExpiredText, bacCheckboxes, mtmCheckboxes } = fields;
  const { link: linkSubmit, iconName: iconNameSubmit, colorScheme: colorSchemeSubmit } = submitCTA.fields;
  const { href: orderConfirmationUrl, text: ctaNameSubmit } = linkSubmit.value;
  const arvatoWidget = useRef();
  const regulationText = useRef({});
  const { bankCards, bankCardsLoaded, selectedBankCard, setSelectedBankCard, showCardForm, setShowCardForm, getBankCards } = useFetchBankCards({
    onFetchEmpty() {
      arvatoWidget.current && arvatoWidget.current.init();
    }
  });
  const [submitting, setSubmitting] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [agreedToTermsHasError, setAgreedToTermsHasError] = useState(false);
  const [agreeToTermsDisabled, setAgreeToTermsDisabled] = useState(false);
  const [savePayment, setSavePayment] = useState(false);
  const [savePaymentHasError, setSavePaymentHasError] = useState(false);
  const [savePaymentDisabled, setSavepaymentDisabled] = useState(false);
  const [oldDriver, setOldDriver] = useState(false);
  const [oldDriverHasError, setOldDriverHasError] = useState(false);
  const [oldDriverDisabled, setOldDriverDisabled] = useState(false);
  const [agreeToItaly, setAgreeToItaly] = useState(false);
  const [agreeToItalyHasError, setAgreeToItalyHasError] = useState(false);
  const [agreeToItalyDisabled, setAgreeToItalyDisabled] = useState(false);
  const [agreeTo14DaysWithdraw, setAgreeTo14DaysWithdraw] = useState(false);
  const [agreeTo14DaysWithdrawHasError, setAgreeTo14DaysWithdrawHasError] = useState(false);
  const [agreeTo14DaysWithdrawDisabled, setAgreeTo14DaysWithdrawDisabled] = useState(false);
  const [editCard, setEditCard] = useState(null);
  const [showRemoveCardModal, setShowRemoveCardModal] = useState(false);
  const [removeCardError, setRemoveCardError] = useState(null);
  const [error, setError] = useState(null);
  const checkboxes = useMemo(() => (pdpPlanType === planType.BaC ? bacCheckboxes : mtmCheckboxes), [pdpPlanType]);
  const isSubmitDisabled = useMemo(() => {
    if (submitting || isEmptyObject(pdpOrderPreview)) {
      return true;
    }
    if (lid || lno) {
      return isEmptyObject(pdpSummary);
    }
    return pdpOutOfStock;
  }, [submitting, pdpOrderPreview, lid, lno, pdpOutOfStock, pdpSummary]);
  useEffect(() => {
    getBankCards();
    return () => {
      webStorage.clear(S_GEAR_SAVE_PAYMENT);
    };
  }, []);
  useEffect(() => {
    webStorage.set(S_GEAR_SAVE_PAYMENT, savePayment);
  }, [savePayment]);
  useEffect(() => {
    setSavePayment(pdpSavePayment);
    setSavepaymentDisabled(pdpSavePayment);
  }, [pdpSavePayment]);
  const checkboxMap = {
    "old driver": [oldDriver, setOldDriver, oldDriverHasError, setOldDriverHasError, oldDriverDisabled, setOldDriverDisabled],
    "terms and conditions": [agreeToTerms, setAgreeToTerms, agreedToTermsHasError, setAgreedToTermsHasError, agreeToTermsDisabled, setAgreeToTermsDisabled],
    "save payment": [savePayment, setSavePayment, savePaymentHasError, setSavePaymentHasError, savePaymentDisabled, setSavepaymentDisabled],
    italy: [agreeToItaly, setAgreeToItaly, agreeToItalyHasError, setAgreeToItalyHasError, agreeToItalyDisabled, setAgreeToItalyDisabled],
    "14 days withdraw period": [agreeTo14DaysWithdraw, setAgreeTo14DaysWithdraw, agreeTo14DaysWithdrawHasError, setAgreeTo14DaysWithdrawHasError, agreeTo14DaysWithdrawDisabled, setAgreeTo14DaysWithdrawDisabled]
  };
  const regulationCheckItems = ["old driver", "italy"];
  const buildRegulationText = (name, checked, label) => {
    if (regulationCheckItems.includes(name)) {
      if (checked) {
        regulationText.current[name] = html2Text(label);
      } else {
        delete regulationText.current[name];
      }
    }
  };
  const buildCheckbox = ck => {
    const { name, label, body } = ck.fields;
    const [checked, setChecked, error, setError, disabled] = checkboxMap[name.value.toLowerCase()];
    return {
      label: label.value,
      infoText: body.value,
      disabled,
      error,
      checked,
      onChange: checked => {
        buildRegulationText(name.value.toLowerCase(), checked, label.value);
        setChecked(checked);
        setError(false);
      }
    };
  };
  const submitButtonProps = {
    buttonText: ctaNameSubmit,
    iconName: iconNameSubmit,
    colorScheme: colorSchemeSubmit,
    isDisabled: isSubmitDisabled,
    callback: async evt => {
      let isValid = true;
      let consentIds = [];
      for (const ck of checkboxes) {
        const { name, required, consents } = ck.fields;
        let [checked, , , setError] = checkboxMap[name.value];
        console.log(name.value, required.value, checked.toString());
        if (required.value && !checked) {
          isValid = false;
          setError(true);
        }
        for (const cs of consents) {
          if (!consentIds.includes(cs.id)) {
            consentIds.push(cs.id);
          }
        }
      }
      if (!selectedBankCard && !showCardForm) {
        setError(paymethodMissingMessage.value);
        isValid = false;
      }
      if (isValid) {
        setSubmitting(true);
        try {
          if (selectedBankCard) {
            if (consentIds.length > 0) {
              await multiBindConsents(user?.email, user.countryId, consentIds);
            }
            await onSubmit({ isValid, paymentRegistrationToken: selectedBankCard.data.registrationNo, bankCardId: selectedBankCard.data.id, regulationCheck: regulationText.current });
            setSubmitting(false);
          } else if (showCardForm) {
            arvatoWidget.current.submit();
          }
        } catch {
          setSubmitting(false);
        }
      }
    }
  };
  const onRemoveCardClick = (evt, card) => {
    evt.stopPropagation();
    setEditCard(card);
    setShowRemoveCardModal(true);
  };
  const onBankCardChange = option => {
    console.log("selected bankcard", option);
    setSelectedBankCard(option);
    setShowCardForm(false);
    setError(null);
  };
  const addCardCtaProps = {
    buttonText: ctaNameAddCard.value,
    colorScheme: colorSchemeAddCard,
    iconName: iconNameAddCard,
    callback() {
      setShowCardForm(true);
      setSelectedBankCard(null);
      setTimeout(() => {
        arvatoWidget.current.init();
      }, 10);
    }
  };
  const arvatoWidgetProps = {
    arvatoOptions: pdpPlanType === planType.MtM ? mtmArvatoOptions.fields : bacArvatoOptions.fields,
    sepa: pdpPlanType === planType.MtM,
    visible: showCardForm,
    ref: arvatoWidget,
    user: user,
    onSubmit: async paymentToken => {
      await onSubmit({ isValid: true, paymentToken, savePayment, regulationCheck: regulationText.current });
      setSubmitting(false);
    },
    onError() {
      setSubmitting(false);
    }
  };
  const removeCardModalProps = removeCardModal && {
    heading: {
      type: removeCardModal.fields.headingType,
      html: removeCardModal.fields.headingBody
    },
    subHeading: {
      type: removeCardModal.fields.subheadingType,
      html: removeCardModal.fields.subheadingBody
    },
    body: removeCardModal.fields.body.value,
    button: {
      ...{
        colorScheme: removeCardModal.fields.colorScheme,
        iconName: removeCardModal.fields.iconName,
        link: {
          value: {
            text: removeCardModal.fields.ctaName.value
          }
        },
        callback: async evt => {
          await api.user.deleteBankCard(null, { bankCardIds: [editCard.id] });
          setShowRemoveCardModal(false);
          setEditCard(null);
          await getBankCards();
        }
      }
    },
    warningMessage: removeCardError,
    onCloseClicked: () => {
      setShowRemoveCardModal(false);
      setRemoveCardError(null);
    },
    showModal: showRemoveCardModal
  };
  const bankCardLayout = option => {
    if (option.data) {
      return (
        <div className="bank-card-contianer">
          <BankCardInformation {...{ card: option.data, expiredText: cardExpiredText }} />
          <div className="icons">{selectedBankCard !== option && option.data.cardSubType !== bankCardSubTypes.actived && bankCards.length > 1 && <Icon name="Delete" colorScheme="outline-green" className="icon --visible" onClick={evt => onRemoveCardClick(evt, option.data)} />}</div>
        </div>
      );
    } else {
      return <p className="label">{option.label}</p>;
    }
  };
  return (
    <section className="pdp-card-info">
      <div className="title">
        <Heading type={headingType} html={heading} className="heading" />
        <BodyText html={notice} className="notice" />
      </div>
      <div className="payment-wrapper">
        <RadioButtonGroup options={bankCards} html={bankCardLayout} alignment="top" onChange={e => onBankCardChange(e)} selectedOption={selectedBankCard} />
        {!showCardForm && (
          <div className="add-payment-cta">
            <PrimaryLink {...addCardCtaProps} />
          </div>
        )}
        <div className="widget-container">
          <ArvatoWidget {...arvatoWidgetProps} />
        </div>
      </div>
      {bankCardsLoaded &&
        checkboxes.map((c, i) => {
          if (c.fields.name.value === "save payment" && bankCards.length > 0 && !showCardForm) {
            return null;
          } else {
            return (
              <div key={c.fields.name.value} className="checkbox-wrapper">
                <Checkbox {...buildCheckbox(c)} />
              </div>
            );
          }
        })}
      {error && <div className="failed-message">{error}</div>}
      <div className="submit">
        <PrimaryButton {...submitButtonProps} />
      </div>
      <BodyText html={disclaimer.value} className="body-text" />
      <ConfirmationModal {...removeCardModalProps} />
    </section>
  );
};
export default PdpCardInfo;
//
// CardInfo.propTypes = {
//   heading: PropTypes.shape(PropTypeHeading),
//   cardType: PropTypeDropdown,
//   cardIcons: PropTypes.array,
//   cardHolderName: PropTypeInput,
//   cardNumber: PropTypeInput,
//   cardExpiryDate: PropTypeInput,
//   cardCVC: PropTypeInput,
//   termsCheckbox: PropTypeCheckbox,
//   paymentInfoCheckbox: PropTypeCheckbox,
//   submit: PropTypePrimaryButton,
//   disclaimer: PropTypes.object
// };
